import { supabase } from '@/lib/supabase';
import { DailyCashFlow, CashFlowFilters, CashFlowProjection } from '@/types/cash-flow';
import { Database } from '@/integrations/supabase/types';

type Transaction = Database['public']['Tables']['transactions']['Row'];
type PaymentAccount = Database['public']['Tables']['payment_accounts']['Row'];

export class CashFlowService {
  /**
   * Busca todas as transações do usuário no período especificado
   */
  static async fetchTransactions(
    userId: string,
    startDate: Date,
    endDate: Date,
    accountIds?: string[],
    categoryIds?: number[]
  ): Promise<Transaction[]> {
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate.toISOString().split('T')[0])
      .lte('date', endDate.toISOString().split('T')[0])
      .order('date', { ascending: true });

    // Filtrar por contas se especificado
    if (accountIds && accountIds.length > 0) {
      query = query.in('payment_account_id', accountIds);
    }

    // Filtrar por categorias se especificado
    if (categoryIds && categoryIds.length > 0) {
      query = query.in('category_id', categoryIds);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao buscar transações:', error);
      throw new Error('Não foi possível carregar as transações');
    }

    return data || [];
  }

  /**
   * Busca contas de pagamento do usuário
   */
  static async fetchPaymentAccounts(userId: string): Promise<PaymentAccount[]> {
    const { data, error } = await supabase
      .from('payment_accounts')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Erro ao buscar contas:', error);
      throw new Error('Não foi possível carregar as contas');
    }

    return data || [];
  }

  
  /**
   * Calcula o fluxo de caixa diário para um período
   */
  static async calculateDailyCashFlow(
    userId: string,
    filters: CashFlowFilters
  ): Promise<DailyCashFlow[]> {
    const { inicio: startDate, fim: endDate } = filters.periodo;
    const accountIds = filters.contas_selecionadas?.length ? filters.contas_selecionadas : undefined;
    const categoryIds = filters.categorias_selecionadas?.length ? filters.categorias_selecionadas.map(id => parseInt(id)) : undefined;

    // Buscar transações do período
    const transactions = await this.fetchTransactions(
      userId,
      startDate,
      endDate,
      accountIds,
      categoryIds
    );

    // Buscar contas para obter saldo inicial
    const paymentAccounts = await this.fetchPaymentAccounts(userId);
    
    // Calcular saldo inicial (soma dos saldos de todas as contas)
    const initialBalance = paymentAccounts.reduce(
      (sum, account) => sum + (account.current_balance || 0),
      0
    );

    // Gerar array com todos os dias do período
    const daysInPeriod = this.generateDaysArray(startDate, endDate);
    
    // Agrupar transações por dia
    const transactionsByDay = this.groupTransactionsByDay(transactions);
    
        
    // Calcular fluxo de caixa diário
    let runningBalance = initialBalance;
    let runningProjectedBalance: number | undefined = undefined;
    const dailyCashFlow: DailyCashFlow[] = [];

    for (const day of daysInPeriod) {
      const dayTransactions = transactionsByDay[day.toISOString().split('T')[0]] || [];
      const dayStartBalance = runningBalance;
      
      // Calcular totais do dia
      const { recebimentos, pagamentos, transferencias_entrada, transferencias_saida } = 
        this.calculateDayTotals(dayTransactions);
      
      // Calcular saldo final do dia
      const dayEndBalance = dayStartBalance + recebimentos - pagamentos + 
        transferencias_entrada - transferencias_saida;
      
      runningBalance = dayEndBalance;
      
            
      dailyCashFlow.push({
        date: day,
        recebimentos,
        pagamentos,
        transferencias_entrada,
        transferencias_saida,
        saldo_inicial: dayStartBalance,
        saldo_final: dayEndBalance
      });
    }

    return dailyCashFlow;
  }

  /**
   * Gera um array com todos os dias entre duas datas
   */
  private static generateDaysArray(startDate: Date, endDate: Date): Date[] {
    const days: Date[] = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  }

  /**
   * Agrupa transações por dia
   */
  private static groupTransactionsByDay(transactions: Transaction[]): Record<string, Transaction[]> {
    return transactions.reduce((groups, transaction) => {
      const date = transaction.date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
      return groups;
    }, {} as Record<string, Transaction[]>);
  }

  /**
   * Calcula os totais do dia com base nas transações
   */
  private static calculateDayTotals(transactions: Transaction[]) {
    let recebimentos = 0;
    let pagamentos = 0;
    let transferencias_entrada = 0;
    let transferencias_saida = 0;

    transactions.forEach(transaction => {
      const amount = transaction.amount || 0;
      const type = transaction.type;
      
      // Corrigido: usar 'income' e 'expense' que são os tipos corretos no banco
      if (type === 'income') {
        recebimentos += amount;
      } else if (type === 'expense') {
        pagamentos += amount;
      }
      // Nota: tipos 'transferencia_entrada' e 'transferencia_saida' não existem no banco
      // Transferências são tratadas como income/expense entre contas
    });

    return {
      recebimentos,
      pagamentos,
      transferencias_entrada,
      transferencias_saida
    };
  }
}
